import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { KeyRound, CheckCircle2 } from "lucide-react";
import { ShopLayout } from "../../components/shop/ShopLayout";
import { shopApi, formatApiError } from "../../lib/shopApi";
import { PasswordInput } from "../../components/shop/PasswordInput";

const inputCls = "w-full bg-[#1C1C1C] border border-[#C9A24B]/20 rounded-xl px-4 py-3.5 pr-12 focus:outline-none focus:border-[#C9A24B] transition-colors";

const ResetPasswordPage = () => {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords don't match");
      return;
    }
    setBusy(true);
    try {
      await shopApi.post("/account/reset-password", { token, new_password: password });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err, "Something went wrong — please try again"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ShopLayout title="Reset Password | Loctician Carlocs">
      <div className="max-w-sm mx-auto px-5 py-14 md:py-20" data-testid="reset-password-page">
        <div className="text-center mb-8">
          <KeyRound className="w-9 h-9 text-[#C9A24B] mx-auto mb-4" />
          <h1 className="font-serif text-3xl mb-2">Reset Password</h1>
          <p className="text-[#F5EFE3]/50 text-sm">Choose a new password for your account.</p>
        </div>

        {done ? (
          <div className="bg-[#232323] border border-[#C9A24B]/15 rounded-2xl p-6 text-center" data-testid="reset-success">
            <CheckCircle2 className="w-8 h-8 text-[#C9A24B] mx-auto mb-4" />
            <p className="text-sm text-[#F5EFE3]/80 mb-5">Your password has been updated.</p>
            <Link
              to="/account/login"
              data-testid="reset-login-link"
              className="inline-block bg-[#C9A24B] text-[#1C1C1C] px-8 py-3.5 rounded-full text-xs font-bold uppercase tracking-widest hover:-translate-y-0.5 transition-all duration-300"
            >
              Sign In
            </Link>
          </div>
        ) : !token ? (
          <div className="bg-[#232323] border border-[#C9A24B]/15 rounded-2xl p-6 text-center" data-testid="reset-invalid">
            <p className="text-sm text-[#F5EFE3]/80 mb-5">This reset link is invalid. Please request a new one.</p>
            <Link to="/account/forgot-password" className="text-[#C9A24B] underline underline-offset-4 text-sm" data-testid="reset-request-new-link">
              Request a new link
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-[#232323] border border-[#C9A24B]/15 rounded-2xl p-6 space-y-4">
            <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="New password (min 6 characters)" data-testid="reset-password-input" toggleTestId="reset-password-toggle" className={inputCls} />
            <PasswordInput value={confirm} onChange={(e) => setConfirm(e.target.value)} required placeholder="Confirm new password" data-testid="reset-password-confirm" toggleTestId="reset-confirm-toggle" className={inputCls} />
            {error && <p className="text-red-400 text-sm" data-testid="reset-error">{error}</p>}
            <button
              type="submit"
              disabled={busy}
              data-testid="reset-submit-btn"
              className="w-full bg-[#C9A24B] text-[#1C1C1C] py-4 rounded-full text-xs font-bold uppercase tracking-widest hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50"
            >
              {busy ? "Updating…" : "Update Password"}
            </button>
          </form>
        )}
      </div>
    </ShopLayout>
  );
};

export default ResetPasswordPage;
